import type {
  ProductVideoPromptOutput,
  StoryboardRow,
  StoryboardAvoidPitfalls,
  StoryboardAudioStyle,
  StoryboardGlobalSetting,
  StoryboardVideoStyle,
} from "~/types"
import {
  DEFAULT_VIDEO_CHANNEL,
  getVideoDurationRange,
} from "~/common/constants"
import { formatDurationSeconds, roundToDecimals } from "~/common/utils"

export type { StoryboardRow } from "~/types"
export type StoryboardPayload = ProductVideoPromptOutput
type ParsedStoryboardPayload = StoryboardPayload | null
type JsonObject = Record<string, unknown>
type ShotTimelineItem<Id extends number | string> = {
  endTime: number
  id: Id
  startTime: number
}

export const validateStoryboardContent = (rows: StoryboardRow[]) => {
  const visualErrorShotNumbers = rows
    .filter((row) => !row.visual_description.trim())
    .map((row) => row.shot_number)
  return {
    visualErrorShotNumbers,
    errorMessage: visualErrorShotNumbers.length
      ? `分镜 ${visualErrorShotNumbers[0]} 的画面动作描述不能为空`
      : "",
  }
}

export const validateShotTimeline = <Id extends number | string>(
  shots: ShotTimelineItem<Id>[],
  model: API.Task.CreateVideoChannel = DEFAULT_VIDEO_CHANNEL,
) => {
  const timeErrorIds: Id[] = []
  let errorMessage = ""

  shots.forEach((shot, index) => {
    const hasInvalidRange =
      !Number.isFinite(shot.startTime) ||
      !Number.isFinite(shot.endTime) ||
      shot.startTime < 0 ||
      shot.endTime <= shot.startTime

    if (hasInvalidRange) {
      timeErrorIds.push(shot.id)
      errorMessage ||= `分镜 ${index + 1} 的起止时间不正确`
    }
  })

  const firstShot = shots[0]

  if (firstShot && roundToDecimals(firstShot.startTime) !== 0) {
    timeErrorIds.push(firstShot.id)
    errorMessage ||= "第一个分镜必须从 0 秒开始"
  }

  shots.slice(1).forEach((shot, index) => {
    const previousShot = shots[index]

    if (
      previousShot &&
      roundToDecimals(previousShot.endTime) !== roundToDecimals(shot.startTime)
    ) {
      timeErrorIds.push(previousShot.id, shot.id)
      errorMessage ||= `分镜 ${index + 1} 与分镜 ${index + 2} 的时间未衔接`
    }
  })

  const lastShot = shots.at(-1)
  const durationRange = getVideoDurationRange(model)

  if (
    lastShot &&
    (lastShot.endTime < durationRange.MIN ||
      lastShot.endTime > durationRange.MAX)
  ) {
    timeErrorIds.push(lastShot.id)
    errorMessage ||= `最后一个分镜的结束时间必须为 ${durationRange.MIN}-${durationRange.MAX} 秒`
  }

  return {
    errorMessage,
    timeErrorIds: Array.from(new Set(timeErrorIds)),
  }
}

const extractJsonBlock = (value: string) => {
  const trimmedValue = value.trim()
  const objectStartIndex = trimmedValue.indexOf("{")
  const arrayStartIndex = trimmedValue.indexOf("[")
  const jsonStartIndex =
    objectStartIndex === -1
      ? arrayStartIndex
      : arrayStartIndex === -1
        ? objectStartIndex
        : Math.min(objectStartIndex, arrayStartIndex)
  const jsonEndIndex =
    jsonStartIndex === arrayStartIndex
      ? trimmedValue.lastIndexOf("]")
      : trimmedValue.lastIndexOf("}")

  if (jsonStartIndex >= 0 && jsonEndIndex > jsonStartIndex) {
    return trimmedValue.slice(jsonStartIndex, jsonEndIndex + 1)
  }

  return trimmedValue
}

const toNumber = (value: unknown) => {
  if (typeof value === "number") return value
  if (typeof value === "string" && value.trim()) {
    const parsedValue = Number(value)
    return Number.isFinite(parsedValue) ? parsedValue : null
  }

  return null
}

const toText = (value: unknown) => {
  return typeof value === "string" ? value : ""
}

const toOptionalText = (value: unknown) => {
  const text = toText(value)

  return text.trim() ? text : undefined
}

const normalizeAudioVoice = (value: unknown) => {
  if (typeof value === "string") return value
  if (!Array.isArray(value)) return ""

  return value
    .map((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return ""

      const voice = item as Record<string, unknown>
      const text = toText(voice.text).trim()

      if (!text) return ""

      const speaker = [
        toText(voice.speaker_id),
        toText(voice.speaker_role),
        toText(voice.speech_type),
      ]
        .filter(Boolean)
        .join(" / ")
      const startTime = toNumber(voice.start_time)
      const endTime = toNumber(voice.end_time)
      const time =
        startTime !== null && endTime !== null
          ? `${startTime.toFixed(1)}s-${endTime.toFixed(1)}s`
          : ""
      const prefix = [speaker, time].filter(Boolean).join("，")

      return prefix ? `${prefix}：${text}` : text
    })
    .filter(Boolean)
    .join("\n")
}

const toBoolean = (value: unknown) => {
  if (typeof value === "boolean") return value
  if (typeof value !== "string") return false

  const normalizedValue = value.trim().toLowerCase()

  return ["1", "true", "yes", "y", "是", "有", "有口播"].includes(
    normalizedValue,
  )
}

const parseJsonObject = (value: string) => {
  try {
    const parsed = JSON.parse(extractJsonBlock(value)) as unknown

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null
    }

    return parsed as JsonObject
  } catch {
    return null
  }
}

const emptyStoryboardVideoStyle: StoryboardVideoStyle = {
  video_type: "",
  visual_tone: "",
  main_color: "",
  rhythm: "",
  hook_type: "",
}

const emptyStoryboardAudioStyle: StoryboardAudioStyle = {
  bgm_spec: "",
  has_voiceover: false,
  voice_tone: "",
}

const emptyStoryboardGlobalSetting: StoryboardGlobalSetting = {
  voice_tone: "",
  master_prompt: "",
  negative_prompt: "",
  global_visual_context: "",
  motion_bucket: 5,
  fps: 30,
}

const emptyStoryboardAvoidPitfalls: StoryboardAvoidPitfalls = {
  pitfall_material: "",
  pitfall_environment: "",
  pitfall_independence_check: "",
}

const normalizeStoryboardVideoStyle = (
  value: unknown,
): StoryboardVideoStyle => {
  if (!value || typeof value !== "object")
    return { ...emptyStoryboardVideoStyle }

  const currentValue = value as Record<string, unknown>

  return {
    video_type: toText(currentValue.video_type),
    visual_tone: toText(currentValue.visual_tone),
    main_color: toText(currentValue.main_color),
    rhythm: toText(currentValue.rhythm),
    hook_type: toText(currentValue.hook_type),
  }
}

const normalizeStoryboardAudioStyle = (
  value: unknown,
): StoryboardAudioStyle => {
  if (!value || typeof value !== "object")
    return { ...emptyStoryboardAudioStyle }

  const currentValue = value as Record<string, unknown>

  return {
    bgm_spec: toText(currentValue.bgm_spec),
    has_voiceover: toBoolean(currentValue.has_voiceover),
    voice_tone: toText(currentValue.voice_tone),
  }
}

const normalizeStoryboardGlobalSetting = (
  value: unknown,
): StoryboardGlobalSetting => {
  if (!value || typeof value !== "object")
    return { ...emptyStoryboardGlobalSetting }

  const currentValue = value as Record<string, unknown>
  const motionBucket = toNumber(currentValue.motion_bucket)
  const fps = toNumber(currentValue.fps)

  return {
    master_prompt: toText(currentValue.master_prompt),
    negative_prompt: toText(currentValue.negative_prompt),
    global_visual_context: toText(currentValue.global_visual_context),
    voice_tone: toText(currentValue.voice_tone),
    motion_bucket: motionBucket ?? emptyStoryboardGlobalSetting.motion_bucket,
    fps: fps ?? emptyStoryboardGlobalSetting.fps,
  }
}

const normalizeStoryboardAvoidPitfalls = (
  value: unknown,
): StoryboardAvoidPitfalls => {
  if (!value || typeof value !== "object")
    return { ...emptyStoryboardAvoidPitfalls }

  const currentValue = value as Record<string, unknown>

  return {
    pitfall_material: toText(currentValue.pitfall_material),
    pitfall_environment: toText(currentValue.pitfall_environment),
    pitfall_independence_check: toText(currentValue.pitfall_independence_check),
  }
}

const getShotSpecParts = (value: unknown) => {
  const shotSpec = toText(value)
  const [shotSize = "", cameraAngle = "", cameraMovement = ""] = shotSpec
    .split("/")
    .map((item) => item.trim())

  return {
    shotSize,
    cameraAngle,
    cameraMovement,
  }
}

const buildShotSpec = (row: {
  camera_angle: string
  camera_movement: string
  focal_depth: string
  lighting: string
  shot_size: string
  shot_spec?: string
}) => {
  if (row.shot_spec?.trim()) return row.shot_spec.trim()

  return [
    row.shot_size,
    row.camera_angle,
    row.camera_movement,
    row.focal_depth,
    row.lighting,
  ]
    .map((item) => item.trim())
    .filter(Boolean)
    .join(" / ")
}

const normalizeStoryboardRow = (row: unknown) => {
  if (!row || typeof row !== "object") return null

  const currentRow = row as Record<string, unknown>
  const rawShotSpec = toText(currentRow.shot_spec)
  const legacyShotSpec = getShotSpecParts(rawShotSpec)
  const shotNumber = toNumber(currentRow.shot_number)
  const startTime = toNumber(currentRow.start_time)
  const endTime = toNumber(currentRow.end_time)
  const duration = toNumber(currentRow.duration)
  const safeDuration =
    duration ??
    (startTime !== null && endTime !== null
      ? Math.max(0, endTime - startTime)
      : null)

  if (shotNumber === null || startTime === null || safeDuration === null) {
    return null
  }

  const safeEndTime = endTime ?? startTime + safeDuration
  const bgmSpec = toOptionalText(currentRow.bgm_spec)
  const audioMix = toOptionalText(currentRow.audio_mix)
  const musicStage = toOptionalText(currentRow.music_stage)
  const audioSync = toOptionalText(currentRow.audio_sync)
  const soundEffects = toOptionalText(currentRow.sound_effects)

  const normalizedRow = {
    shot_number: shotNumber,
    start_time: startTime,
    end_time: safeEndTime,
    duration: safeDuration,
    marketing_role:
      toText(currentRow.marketing_role) || toText(currentRow.content),
    hook_level: toText(currentRow.hook_level),
    visual_description:
      typeof currentRow.visual_description === "string"
        ? currentRow.visual_description
        : toText(currentRow.generation_prompt),
    shot_size: toText(currentRow.shot_size) || legacyShotSpec.shotSize,
    camera_angle: toText(currentRow.camera_angle) || legacyShotSpec.cameraAngle,
    camera_movement:
      toText(currentRow.camera_movement) || legacyShotSpec.cameraMovement,
    focal_depth: toText(currentRow.focal_depth),
    lighting: toText(currentRow.lighting),
    transition: toText(currentRow.transition) || "直接切镜",
    focus_action: toText(currentRow.focus_action),
    audio_voice: normalizeAudioVoice(currentRow.audio_voice),
    ...(bgmSpec ? { bgm_spec: bgmSpec } : {}),
    ...(audioMix ? { audio_mix: audioMix } : {}),
    ...(musicStage ? { music_stage: musicStage } : {}),
    ...(audioSync ? { audio_sync: audioSync } : {}),
    ...(soundEffects ? { sound_effects: soundEffects } : {}),
  }

  return {
    ...normalizedRow,
    shot_spec: buildShotSpec({
      ...normalizedRow,
      shot_spec: rawShotSpec,
    }),
  } satisfies StoryboardRow
}

const isStoryboardRow = (value: StoryboardRow | null) => {
  return value !== null
}

export const getStoryboardHasVoiceover = (rows: StoryboardRow[]) => {
  return rows.some((row) => row.audio_voice.trim().length > 0)
}

export const buildStoryboardPromptText = (
  payload: StoryboardPayload,
  rows = payload.rows,
) => {
  const nextPayload: StoryboardPayload = {
    global_setting: payload.global_setting,
    video_style: payload.video_style,
    audio_style: {
      ...payload.audio_style,
      has_voiceover: getStoryboardHasVoiceover(rows),
    },
    rows,
    avoid_pitfalls: payload.avoid_pitfalls,
  }

  return JSON.stringify(nextPayload, null, 2)
}

const getJsonObject = (value: unknown) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }

  return value as JsonObject
}

const mergeStoryboardRowsFromSource = (
  sourceRows: unknown,
  rows: StoryboardRow[],
) => {
  const rawRows = Array.isArray(sourceRows) ? sourceRows : []

  return rows.map((row, index) => {
    const rawRow =
      rawRows.find((item) => {
        const currentRow = getJsonObject(item)

        return toNumber(currentRow?.shot_number) === row.shot_number
      }) || rawRows[index]
    const rawRowObject = getJsonObject(rawRow)

    return rawRowObject ? { ...rawRowObject, ...row } : row
  })
}

export const buildStoryboardPromptTextFromSource = (
  source: string,
  rows: StoryboardRow[],
) => {
  const sourcePayload = parseJsonObject(source)

  if (!sourcePayload) return ""

  const audioStyle = getJsonObject(sourcePayload.audio_style) || {}

  return JSON.stringify(
    {
      ...sourcePayload,
      audio_style: {
        ...audioStyle,
        has_voiceover: getStoryboardHasVoiceover(rows),
      },
      rows: mergeStoryboardRowsFromSource(sourcePayload.rows, rows),
    },
    null,
    2,
  )
}

// 当前接受后端结构 { global_setting, video_style, audio_style, rows, avoid_pitfalls }。
export const parseStoryboardPayload = (
  value: string,
): ParsedStoryboardPayload => {
  const parsed = parseJsonObject(value)

  if (!parsed) {
    return null
  }

  const payload = parsed as Record<string, unknown>

  try {
    if (!Array.isArray(payload.rows)) {
      return null
    }

    const rows = payload.rows
      .map((row) => normalizeStoryboardRow(row))
      .filter(isStoryboardRow)

    if (!rows.length) {
      return null
    }

    const audioStyle = normalizeStoryboardAudioStyle(payload.audio_style)

    return {
      global_setting: normalizeStoryboardGlobalSetting(payload.global_setting),
      video_style: normalizeStoryboardVideoStyle(payload.video_style),
      audio_style: {
        ...audioStyle,
        has_voiceover: getStoryboardHasVoiceover(rows),
      },
      rows,
      avoid_pitfalls: normalizeStoryboardAvoidPitfalls(payload.avoid_pitfalls),
    }
  } catch {
    return null
  }
}

const formatTextValue = (value: string | undefined, fallback = "无") => {
  return value?.trim() || fallback
}

const getRowAudioSettingItems = (
  row: StoryboardRow,
): Array<[string, string]> => {
  const items = [
    ["BGM类型", row.bgm_spec],
    ["音量权重", row.audio_mix],
    ["音乐阶段", row.music_stage],
    ["同步关系", row.audio_sync],
    ["音效", row.sound_effects],
  ] satisfies Array<[string, string | undefined]>

  return items.filter((item): item is [string, string] =>
    Boolean(item[1]?.trim()),
  )
}

const buildVideoAnalysisPromptText = (payload: StoryboardPayload) => {
  const rows = payload.rows
  const videoStyle = payload.video_style
  const audioStyle = payload.audio_style
  const hasVoiceover = audioStyle.has_voiceover

  const visualStyleLines = [
    "整体视觉风格：",
    `  1. 视频类型：${formatTextValue(videoStyle.video_type)}`,
    `  2. 视觉风格与调性：${formatTextValue(videoStyle.visual_tone)}`,
    `  3. 视觉主色调: ${formatTextValue(videoStyle.main_color)}`,
    `  4. 剪辑节奏：${formatTextValue(videoStyle.rhythm)}`,
    `  5. 开局钩子：${formatTextValue(videoStyle.hook_type)}`,
  ]

  const audioLines = [
    "音频配置：",
    `  1. 背景音乐：${formatTextValue(audioStyle.bgm_spec)}`,
    `  2. 是否有口播：${hasVoiceover ? "是" : "否"}`,
  ]

  if (hasVoiceover) {
    audioLines.push(`  3. 口播调性：${formatTextValue(audioStyle.voice_tone)}`)
  }

  const rowLines = rows.flatMap((row, index) => {
    const lines = [
      `分镜${row.shot_number}`,
      `   （1）时间：${formatDurationSeconds(row.start_time)} - ${formatDurationSeconds(row.end_time)}`,
      `   （2）营销目的：${formatTextValue(row.marketing_role)}`,
      `   （3）吸睛度：${formatTextValue(row.hook_level)}`,
      `   （4）镜头描述：${formatTextValue(row.shot_spec)}`,
      `   （5）画面描述：${formatTextValue(row.visual_description)}`,
      `   （6）转场机制：${formatTextValue(row.transition, "直接切镜")}`,
      `   （7）展示产品及动作：${formatTextValue(row.focus_action)}`,
    ]

    let nextLineIndex = 8

    for (const [label, value] of getRowAudioSettingItems(row)) {
      lines.push(`   （${nextLineIndex}）${label}：${formatTextValue(value)}`)
      nextLineIndex += 1
    }

    if (hasVoiceover) {
      lines.push(
        `   （${nextLineIndex}）口播脚本：${formatTextValue(row.audio_voice)}`,
      )
    }

    if (index > 0) {
      lines.unshift("")
    }

    return lines
  })

  return [
    ...visualStyleLines,
    "",
    ...audioLines,
    "",
    "逐个分镜拆解：",
    ...rowLines,
  ].join("\n")
}

export const formatVideoAnalysisPromptText = (value: string) => {
  const payload = parseStoryboardPayload(value)

  return payload ? buildVideoAnalysisPromptText(payload) : value
}

export const getStoryboardShotSummary = (row: StoryboardRow) => {
  return row.focus_action || row.marketing_role || row.shot_spec || "分镜内容"
}

const formatDisplayTextValue = (value: string | undefined) => {
  return value?.trim() || "-"
}

// 给任务列表和完整提示词弹窗复制用，只展示分镜内的可读内容，不带全局配置。
export const buildStoryboardDisplayText = (rows: StoryboardRow[]) => {
  return rows
    .map((row) => {
      return [
        `分镜 ${row.shot_number} · 时长 ${formatDurationSeconds(row.duration)} · ${getStoryboardShotSummary(row)}`,
        `画面动作描述：${formatDisplayTextValue(row.visual_description)}`,
        `该镜头的营销目的：${formatDisplayTextValue(row.marketing_role)}`,
        ...getRowAudioSettingItems(row).map(([label, value]) => {
          return `${label}：${formatDisplayTextValue(value)}`
        }),
        `口播内容（若不填，则视为该分镜无口播内容）：${formatDisplayTextValue(row.audio_voice)}`,
      ].join("\n")
    })
    .join("\n\n")
}
